import type {
  ApiErrorKind,
  ApiErrorResponse,
  ValidationErrors,
} from "./api-error.types";

/** Every field message, in field order — for banners that list all failures. */
export function flattenValidationErrors(errors: ValidationErrors): string[] {
  return Object.values(errors).flat();
}

const FALLBACK_BY_KIND: Partial<Record<ApiErrorKind, string>> = {
  NETWORK_ERROR: "Network error",
  MALFORMED_RESPONSE: "The server returned an unexpected response",
};

/**
 * The one human-readable line for a failure.
 *
 * On a 422 the field message always wins, even though the envelope carries a
 * `message`. That is not a preference — `Handler::handleException()` builds the
 * validation branch as `rest()->validationError()->data($e->errors())` and
 * never calls `->message()`, so `ApiResponse`'s constructor default survives
 * and **every validation failure literally says "Success"**.
 *
 * Testing for that string would work today and break the moment a second locale
 * is enabled (the default is `__('site.success')`). Preferring the field message
 * is locale-proof, and it is the more useful line anyway.
 */
export function normalizeApiErrorMessage(response: ApiErrorResponse): string {
  if (response.errors) {
    const [first] = flattenValidationErrors(response.errors);
    if (first) return first;
  }

  if (typeof response.message === "string" && response.message.length > 0) {
    return response.message;
  }

  return FALLBACK_BY_KIND[response.kind] ?? "Request failed";
}
